import axios from "axios";

import { createApi } from "@reduxjs/toolkit/query/react";

import getTenantId from "../../components/Tenent";
import {
  showAccessDeniedModal,
  showSessionExpiredModal,
} from "../slices/authSlice";

const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ALGO_API,
  withCredentials: true,
  headers: { accept: "application/json" },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["x-tenant-id"] = getTenantId();
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/login") {
      getStore().dispatch(showSessionExpiredModal(window.location.pathname));
    }
    return Promise.reject(err);
  },
);

const axiosBaseQuery =
  () =>
  async ({ url, method, body, params, ...requestOpts }) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;
      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return { error: { status, data: errorData || axiosError.message } };
    }
  };

export const fileProcessing = createApi({
  reducerPath: "fileprocess",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["fileprocess", "parallax-activities"],
  endpoints: (builder) => ({
    getPrimaryDetails: builder.query({
      query: (configName) => {
        return {
          url: `primary_details_retreive/`,
          params: { config_name: configName },
        };
      },
      providesTags: ["fileprocess"],
    }),
    getColumns: builder.query({
      query: (configName) => {
        return {
          url: `get_columns/`,
          params: { config_name: configName },
        };
      },
      providesTags: ["fileprocess"],
    }),
    getAllConfigurations: builder.query({
      query: () => {
        return {
          url: `get_all_configurations/`,
        };
      },
      providesTags: ["config"],
    }),
    createConfigName: builder.mutation({
      query: (data) => {
        return {
          url: `create_config_name/`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["config"],
    }),
    uploadFile: builder.mutation({
      query: ({ file, configName }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `file_upload/?config_name=${configName}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    getDownloadUrls: builder.query({
      query: ({ id }) => {
        return {
          url: `/cre/get_file_url_cre`,
          params: { id },
        };
      },
    }),
    getArchivedDownloadUrls: builder.query({
      query: ({ id }) => {
        return {
          url: `/get_file_url`,
          params: { id },
        };
      },
    }),
    getUploadedFiles: builder.query({
      query: (args) => {
        return {
          url: `/cre/get_files_category_template_wise`,
          params: args?.category_id ? { category_id: args.category_id } : {},
        };
      },

      providesTags: ["file-upload"],
    }),
    getArchivedUploadedFiles: builder.query({
      query: () => {
        return {
          url: `/get_file_pc_og`,
        };
      },
    }),

    uploadFiles: builder.mutation({
      query: (body) => {
        return {
          url: `/cre/post_files_template_selected`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["file-upload"],
    }),
    deleteUploadedFileData: builder.mutation({
      query: ({ id, purge }) => ({
        url: `/cre/delete_input_file_mat_individual`,
        method: "DELETE",
        params: {
          id,
          purge_data: purge,
        },
      }),
      invalidatesTags: ["file-upload"],
    }),
    deleteArchivedUploadedFileData: builder.mutation({
      query: ({ fileName, purge }) => ({
        url: `/delete_input_file_mat/`,
        method: "DELETE",
        params: {
          fileName,
          purge_data: purge,
        },
      }),
      invalidatesTags: ["file-upload"],
    }),
    ingestFileData: builder.mutation({
      query: (input) => ({
        url: `/process_central_file/`,
        method: "POST",
        body: typeof input === "string" ? { fileName: input } : input,
      }),
      invalidatesTags: ["IngestedFiles"],
    }),

    getCategories: builder.query({
      query: () => {
        return {
          url: `/get_categories/`,
        };
      },
    }),
    getUnits: builder.query({
      query: ({ file_id }) => {
        return {
          url: `/cre/get_units?file_id=${file_id}`,
        };
      },
    }),
    getUnMatchedNodes: builder.query({
      query: (fileName) => {
        return {
          url: `cre/get_unmatched_nodes?file_name=${fileName}`,
        };
      },
    }),

    getIngestedFiles: builder.query({
      query: () => {
        return {
          url: `cre/get_files_category_template_wise`,
        };
      },
      providesTags: ["IngestedFiles"],
    }),
    getUniqueActivites: builder.query({
      query: ({
        file_id,
        page = 1,
        page_size,
        category_ids,
        carbon_emission,
        input_unit,
        matched_activity_unit,
        search,
      }) => {
        return {
          method: "GET",
          url: "cre/cre_get_activities_filename",
          params: {
            file_id,
            page,
            page_size,
            category_ids,
            carbon_emission,
            input_unit,
            matched_activity_unit,
            search,
          },
        };
      },
      providesTags: ["matches"],
    }),
    getAlternateMatches: builder.query({
      query: ({ id, filename }) => {
        return {
          method: "GET",
          url: "/cre/cre_alternate_matches",
          params: { id, filename },
        };
      },
      providesTags: ["matches"],
    }),
    updateMatch: builder.mutation({
      query: (body) => {
        return {
          method: "POST",
          url: `/cre/cre_modify_match`,
          body,
        };
      },
      invalidatesTags: ["matches"],
    }),
    getUnitActivityExplorer: builder.query({
      query: ({
        search_text = "",
        activity_id,
        page = 1,
        limit,
        activity_unit,
        region,
        file_name,
      }) => ({
        url: "/unit_activity_explorer/",
        method: "GET",
        params: {
          search_text,
          region,
          activity_id,
          activity_unit,
          page,
          limit,
          file_name,
        },
      }),
    }),
    getRegions: builder.query({
      query: ({
        search_text = "",
        activity_id,
        activity_unit,
        region_text,
        file_name,
      }) => ({
        url: "/unit_activity_explorer/regions/",
        method: "GET",
        params: {
          search_text,
          activity_id,
          activity_unit,
          region_text,
          file_name,
        },
      }),
    }),
    startInstance: builder.mutation({
      query: (body = {}) => {
        return {
          method: "POST",
          url: `/spin_instance/`,
          body,
        };
      },
    }),
    runAIMatches: builder.mutation({
      query: (body) => {
        return {
          url: "/run_ai_matches/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["matches"],
    }),
    ingestFile: builder.mutation({
      query: (body) => {
        return {
          url: "/cre/uc_process_central_file/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["file-upload"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const { activitiesApi } = await import("./emissionActivities");
        dispatch(activitiesApi.util.invalidateTags(["summary"]));
      },
    }),
    mapSheetsWithTemplates: builder.mutation({
      query: (body) => {
        return {
          url: "/cre/sheet_wise_template_validation",
          method: "POST",
          body,
        };
      },
      // invalidatesTags: ["matches"],
    }),
    getPCFmatches: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: `/pcf_data_view`,
          params,
        };
      },
    }),
    getTemplatesCategoryWise: builder.query({
      query: ({ category_id, file_id }) => ({
        url: `/cre/get_templates_category_wise`,
        params: { category_id, file_id },
      }),
    }),
    getCategoriesConfigs: builder.query({
      query: () => ({
        url: `/cre/get_categories_configurations`,
      }),
    }),

    getTemplatesByCategory: builder.query({
      query: (categorySlug) => ({
        url: `/cre/get_templates_category_wise?category_id=${categorySlug}`,
      }),
    }),
    getCombinedIngestedFiles: builder.query({
      query: () => {
        return {
          url: `/cre/get_file_names_combined`,
          //gives files from both matching algo v1 and v2
        };
      },
      providesTags: ["IngestedFiles"],
    }),
    getSheetDetails: builder.query({
      query: ({ file_id }) => ({
        url: `/cre/get_sheet_names`,
        params: { file_id },
      }),
    }),
    getTemplatesUserWise: builder.query({
      query: () => {
        return {
          url: `/cre/get_formats_user`,
        };
      },
    }),
    getFormats: builder.query({
      query: () => ({
        url: `/cre/get_formats`,
      }),
    }),
    getEligibleRoles: builder.query({
      query: ({ approver_eligible, submitter_eligible }) => ({
        url: `/cre/approver_submitter_suitability`,
        params: {
          ...(approver_eligible && { approver_eligible: approver_eligible }),
          ...(submitter_eligible && { submitter_eligible: submitter_eligible }),
        },
      }),
      providesTags: ["eligible-roles"],
    }),
    getApprovers: builder.query({
      query: ({ node_ids } = {}) => {
        const hasNodes = Array.isArray(node_ids) && node_ids.length > 0;

        return {
          url: `/cre/get_approvers`,
          ...(hasNodes && {
            params: {
              node_id: node_ids.join(","),
            },
          }),
        };
      },
      providesTags: ["approver"],
    }),

    getRolesData: builder.query({
      query: () => ({
        url: `/cre/get_submitters`,
      }),
      providesTags: ["roles-data"],
    }),
    createApprovers: builder.mutation({
      query: (payload) => ({
        url: `/cre/set_approver_config`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["approver", "eligible-roles"],
    }),
    createSubmitterConfig: builder.mutation({
      query: (payload) => ({
        url: `/cre/save_submitter_config`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["roles-data"],
    }),
    requestForApproval: builder.mutation({
      query: (fileId) => ({
        url: `/cre/request_for_approval`,
        method: "POST",
        body: { file_id: fileId },
      }),
    }),
    reviewRequest: builder.query({
      query: ({ file_id, status }) => ({
        url: `cre/approver_status_submit`,
        method: "GET",
        params: { file_id, status },
      }),
    }),
    getBvkSolar: builder.query({
      query: ({ taxonomy, start_date, end_date }) => ({
        url: `/cre/get_off_set_deep_dive`,
        params: { taxonomy, start_date, end_date },
      }),
    }),
    getBvkScope2Emissions: builder.query({
      query: ({ taxonomy, start_date, end_date }) => ({
        url: `/cre/get_off_set`,
        params: { taxonomy, start_date, end_date },
      }),
    }),
    deleteApprover: builder.mutation({
      query: (approver_ids) => ({
        url: `/cre/delete_approver`,
        method: "DELETE",
        params: {
          approver_ids: approver_ids,
        },
      }),
      invalidatesTags: ["approver", "eligible-roles"],
    }),
    deleteSubmitter: builder.mutation({
      query: ({ submitter_id, node_id }) => ({
        url: `/cre/delete_submitter`,
        method: "DELETE",
        params: {
          node_id,
          submitter_ids: submitter_id,
        },
      }),
      invalidatesTags: ["roles-data", "submitter-entries"],
    }),
    updateSubmitter: builder.mutation({
      query: (body) => ({
        url: `/cre/update_submitter_entry`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["submitter-entries"],
    }),
    getSubmitterOrApprover: builder.query({
      query: (params) => ({
        url: `/cre/get_submitters_approvers`,
        params,
      }),
      // invalidatesTags: ["roles-data"]
      //TODO: this reset the get api of table
    }),
    getSubmitterEntries: builder.query({
      query: ({ node_id }) => ({
        url: `/cre/get_submitter_entries`,
        params: { node_id },
      }),
      providesTags: ["submitter-entries"],
    }),
    addSubmitter: builder.mutation({
      query: (body) => ({
        url: `/cre/create_submitter_entry`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["submitter-entries"],
    }),

    getUploadApprovers: builder.query({
      query: ({ node_id }) => ({
        url: `/cre/get_upload_approver_entries`,
        params: { node_id },
      }),
    }),

    editSubmitterEntry: builder.mutation({
      query: (body) => ({
        url: `/cre/edit_submitter_entry`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["submitter-entries"],
    }),
    deleteSubmitterEntry: builder.mutation({
      query: (body) => ({
        url: `/cre/delete_submitter_entry`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["submitter-entries"],
    }),
    createUploadApproverEntry: builder.mutation({
      query: (body) => ({
        url: `/cre/create_approver_entry`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["upload-approver-entries"],
    }),
    deleteApproverEntry: builder.mutation({
      query: (body) => ({
        url: `/cre/delete_approver_entry`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["upload-approver-entries"],
    }),
    editApproverEntry: builder.mutation({
      query: (body) => ({
        url: `/cre/edit_approver_entry`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["upload-approver-entries"],
    }),
    getSubmitterFileDetailsById: builder.query({
      query: ({ file_id }) => ({
        url: `/cre/get_submitter_file_details_id`,
        params: { file_id },
      }),
    }),
    aggregateModifyMatch: builder.mutation({
      query: ({
        selected_id,
        group_type,
        group_id,
        activity_key,
        kw_emission_input_items_id,
        activity_group_id,
        node_id,
        category_id,
        category,
        file_name,
        node_ids,
        search_text,
        from_date,
        to_date,
        not_found_only,
        is_pcf,
      }) => ({
        url: `/cre/aggregate_cre_modify_match`,
        method: "POST",
        body: {
          selected_id,
          group_type,
          group_id,
          ...(kw_emission_input_items_id != null && {
            kw_emission_input_items_id: not_found_only ? [kw_emission_input_items_id].flat() : kw_emission_input_items_id,
          }),
          ...(activity_key != null && {
            activity_key: not_found_only ? [activity_key].flat() : activity_key,
          }),
          ...(activity_group_id != null && { activity_group_id }),
          ...(node_id != null && { node_id }),
          ...(category_id != null && { category_id }),
          ...(category && { category }),
          ...(file_name && { file_name }),
          ...(node_ids && { node_ids }),
          ...(search_text && { search_text }),
          ...(from_date && { from_date }),
          ...(to_date && { to_date }),
          ...(not_found_only !== undefined && { not_found_only }),
          ...(is_pcf !== undefined && { is_pcf }),
        },
      }),
      invalidatesTags: ["parallax-activities", "review", "summary"],
    }),
    getViewDataFactorSearch: builder.mutation({
      query: ({
        search_text_factor = "",
        page = 1,
        limit = 20,
        activity_key,
        region,
        source_list,
        units_filter,
        previously_selected,
        year_filter,
        not_found_only,
        is_pcf_search,
      } = {}) => ({
        url: `/cre/unit_activity_explorer_view_data_page`,
        method: "POST",
        body: {
          ...(search_text_factor && { search_text_factor }),
          page,
          limit,
          ...(activity_key != null && { activity_key: not_found_only ? [activity_key].flat() : activity_key }),
          ...(region?.length && { region: region.join(",") }),
          ...(source_list?.length && { source_list: source_list.join(",") }),
          ...(units_filter?.length && { units_filter: units_filter.join(",") }),
          ...(previously_selected !== undefined && { previously_selected }),
          ...(year_filter?.length && { year_filter: year_filter.join(",") }),
          ...(not_found_only !== undefined && { not_found_only }),
          ...(is_pcf_search !== undefined && { is_pcf_search }),
        },
      }),
    }),
    getActivityExplorerFilters: builder.query({
      query: ({ activity_key } = {}) => ({
        url: `/cre/activity_explorer_filters`,
        method: "GET",
        // params: {
        //   ...(activity_key && { activity_key }),
        // },
      }),
    }),
    getUnitsFilter: builder.mutation({
      query: ({ activity_key, not_found_only } = {}) => ({
        url: `/cre/units_filter`,
        method: "POST",
        body: {
          ...(activity_key != null && { activity_key: not_found_only ? [activity_key].flat() : activity_key }),
          ...(not_found_only !== undefined && { not_found_only }),
        },
      }),
    }),
    flagRecords: builder.mutation({
      query: ({
        kw_emission_input_items_id,
        forest_land_agriculture_flag,
        group_type,
        node_id,
        category_id,
        filterParams,
      }) => ({
        url: `/cre/flag_records`,
        method: "POST",
        body: {
          kw_emission_input_items_id,
          forest_land_agriculture_flag,
          group_type,
          ...(node_id != null && { node_id }),
          ...(category_id != null && { category_id }),
          ...Object.fromEntries(Object.entries(filterParams ?? {}).filter(([, v]) => v != null && v !== "")),
        },
      }),
      invalidatesTags: ["parallax-activities"],
    }),
    getNextActivities: builder.query({
      query: ({
        carbon_order,
        search_text,
        month_year,
        category,
        file_name,
        node_ids,
        from_date,
        to_date,
        group_by,
        activity_key,
        last_id,
        last_activity_key,
        parent,
        not_found_only,
      } = {}) => {
        const params = {
          ...(carbon_order && { carbon_order }),
          ...(search_text && { search_text }),
          ...(month_year && { month_year }),
          ...(category && { category }),
          ...(file_name && { file_name }),
          ...(node_ids && { node_ids }),
          ...(from_date && { from_date }),
          ...(to_date && { to_date }),
          ...(group_by && { group_by }),
          ...(activity_key && { activity_key }),
          ...(last_id && { last_id }),
          ...(last_activity_key && { last_activity_key }),
          ...(parent !== undefined && parent !== null && { parent }),
          ...(not_found_only && { not_found_only }),
        };

        return {
          url: `/cre/parallax`,
          params,
        };
      },
      providesTags: ["parallax-activities"],
    }),
    getReviewData: builder.query({
      query: (params) => {
        return {
          url: `/cre/get_data_review`,
          params,
        };
      },
      providesTags: ["review-data", "review"],
    }),
    getReviewDataSummary: builder.query({
      query: (params) => {
        return {
          url: `/cre/review_data_summary`,
          params,
        };
      },
      providesTags: ["summary"],
    }),
    deleteViewData: builder.mutation({
      query: ({ filterParams, ...rest }) => {
        return {
          url: `/cre/delete_item_view_data`,
          body: {
            ...rest,
            ...Object.fromEntries(Object.entries(filterParams ?? {}).filter(([, v]) => v != null && v !== "")),
          },
          method: "DELETE",
        };
      },
      invalidatesTags: ["review", "summary"]
    }),
    deleteSubmitterUpload: builder.mutation({
      query: (params) => {
        return {
          url: `/cre/delete_input_file_mat_individual`,
          params,
          method: 'DELETE'
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const { activitiesApi } = await import("./emissionActivities");
        dispatch(activitiesApi.util.invalidateTags(["submissions", "summary"]));
      },
    }),
    resolveRecords: builder.query({
      query: (params) => {
        return {
          url: `/cre/get_data_review`,
          params,
        }
      }
    }),
    addEmissionComment: builder.mutation({
      query: ({ kw_emission_input_item_id, comment }) => ({
        url: `/cre/add_comment`,
        method: "POST",
        body: { kw_emission_input_item_id, comment },
      }),
      invalidatesTags: ["parallax-activities"],
    }),
    updateEmissionResolveStatus: builder.mutation({
      query: ({ kw_emission_input_item_id, resolved_flag }) => ({
        url: `/cre/update_resolve_status`,
        method: "POST",
        body: { kw_emission_input_item_id, resolved_flag },
      }),
      invalidatesTags: ["parallax-activities", "summary"],
    }),
    ingestMultipleFiles: builder.mutation({
      query: (files) => ({
        url: `/cre/uc_process_multiple_files/`,
        body: files,
        method: 'POST'
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const { activitiesApi } = await import("./emissionActivities");
        dispatch(activitiesApi.util.invalidateTags(["summary"]));
      },
    }),
    uploadMultipleFiles: builder.mutation({
      query: (files) => ({
        url: `/cre/post_multiple_files_template_selected`,
        body: files,
        method: 'POST'
      })
    }),
    evaluateCurrencyFactorUpdate: builder.mutation({
      query: ({ category_id, selected_id, from_date, to_date, activity_based }) => ({
        url: `/cre/evaluate_currency_factor_update`,
        method: "POST",
        body: { category_id, selected_id, from_date, to_date, activity_based },
      }),
    }),
    getCurrencyFactorUpdateStatus: builder.mutation({
      query: (job_id) => ({
        url: `/cre/currency_factor_update_status`,
        method: "GET",
        params: job_id != null ? { job_id } : undefined,
      }),
    }),
    searchLongTailFactor: builder.mutation({
      query: ({ search = "", limit = 20, page = 1, region_list, units_list } = {}) => ({
        url: `/cre/search_long_tail_factor`,
        method: "GET",
        params: {
          ...(search && { search }),
          limit,
          page,
          ...(region_list && { region_list }),
          ...(units_list && { units_list }),
        },
      }),
    }),
  }),
});

export const {
  useGetPrimaryDetailsQuery,
  useGetColumnsQuery,
  useGetAllConfigurationsQuery,
  useCreateConfigNameMutation,
  useUploadFileMutation,
  useLazyGetUploadedFilesQuery,
  useUploadFilesMutation,
  useDeleteUploadedFileDataMutation,
  useIngestFileDataMutation,
  useGetCategoriesQuery,
  useGetUnMatchedNodesQuery,
  useGetIngestedFilesQuery,
  useLazyGetUniqueActivitesQuery,
  useGetAlternateMatchesQuery,
  useUpdateMatchMutation,
  useGetUnitActivityExplorerQuery,
  useStartInstanceMutation,
  useRunAIMatchesMutation,
  useIngestFileForCleanupMutation,
  useGetFileFormatsQuery,
  useGetDownloadUrlsQuery,
  useGetRegionsQuery,
  useGetPCFmatchesQuery,
  useLazyGetTemplatesCategoryWiseQuery,
  useLazyGetCategoriesConfigsQuery,
  useMapSheetsWithTemplatesMutation,
  useIngestFileMutation,
  useLazyGetTemplatesByCategoryQuery,
  useLazyGetArchivedUploadedFilesQuery,
  useGetArchivedDownloadUrlsQuery,
  useDeleteArchivedUploadedFileDataMutation,
  useGetCombinedIngestedFilesQuery,
  useLazyGetSheetDetailsQuery,
  useLazyGetUnitsQuery,
  useGetTemplatesUserWiseQuery,
  useGetFormatsQuery,
  useGetEligibleRolesQuery, 
  useGetApproversQuery,
  useCreateApproversMutation,
  useGetRolesDataQuery,
  useCreateSubmitterConfigMutation,
  useRequestForApprovalMutation,
  useLazyReviewRequestQuery,
  useDeleteApproverMutation,
  useDeleteSubmitterMutation,
  useLazyGetDownloadUrlsQuery,
  useGetSubmitterOrApproverQuery,
  useGetSubmitterEntriesQuery,
  useGetUploadApproversQuery,
  useAddSubmitterMutation,
  useUpdateSubmitterMutation,
  useEditSubmitterEntryMutation,
  useDeleteSubmitterEntryMutation,
  useCreateUploadApproverEntryMutation,
  useDeleteApproverEntryMutation,
  useEditApproverEntryMutation,
  useGetSubmitterFileDetailsByIdQuery,
  useLazyGetSubmitterFileDetailsByIdQuery,
  useGetNextActivitiesQuery,
  useLazyGetNextActivitiesQuery,
  useGetViewDataFactorSearchMutation,
  useAggregateModifyMatchMutation,
  useLazyGetActivityExplorerFiltersQuery,
  useGetActivityExplorerFiltersQuery,
  useGetUnitsFilterMutation,
  useFlagRecordsMutation,
  useGetReviewDataQuery,
  useGetReviewDataSummaryQuery,
  useGetCategoriesConfigsQuery,
  useDeleteSubmitterUploadMutation,
  useDeleteViewDataMutation,
  useResolveRecordsQuery,
  useAddEmissionCommentMutation,
  useUpdateEmissionResolveStatusMutation,
  useIngestMultipleFilesMutation,
  useUploadMultipleFilesMutation,
  useEvaluateCurrencyFactorUpdateMutation,
  useSearchLongTailFactorMutation,
  useGetCurrencyFactorUpdateStatusMutation,
} = fileProcessing;
